/**
 * v2 model store.
 *
 * Separate from the v1 store rather than a mode inside it: the shapes are
 * incompatible and v1 is live production.
 *
 * Edits are optimistic — the cell updates immediately and a recalculation is
 * debounced behind it — because waiting for a round trip per keystroke makes a
 * spreadsheet feel broken. `saveError` surfaces a failed save rather than
 * leaving the user with edits they believe are saved.
 */

import { create } from 'zustand';
import { hydrateForecast } from './hydrateForecast';
import { immer } from 'zustand/middleware/immer';
import type { ModelSettings, OverrideCategory } from '../types/financial-model';
import type {
  AutofixRun,
  BacktestResultV2,
  AttentionItem,
  TabAudit,
  BalanceDiagnosis,
  BalanceFromTextProposal,
  BalanceTextApplied,
  FinancialModelV2,
  ForecastResultV2,
  ModelInputsV2,
  ModelMetadataV2,
  ModelVersionMetaV2,
  PeriodData,
  ReconciliationResultV2,
} from '../types/financial-model-v2';
import { useAuthStore } from './auth';

const RECALC_DEBOUNCE_MS = 600;

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function jsonHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', ...authHeaders() };
}

/** A company found on Companies House. */
export interface CompanyHit {
  number: string;
  name: string;
  status: string;
  type: string;
  incorporated: string;
  address: string;
  sicCodes: string[];
}

/** One accounts filing of a company, with the formats Companies House holds it in. */
export interface AccountsFiling {
  transactionId: string;
  filed: string;
  madeUpTo: string;
  kind: string;
  description: string;
  pages: number | null;
  formats: string[];
}

export interface CompanySearchFilters {
  status?: string;
  type?: string;
  sic?: string;
  incorporatedFrom?: string;
  incorporatedTo?: string;
}

async function companiesHouse<T>(path: string): Promise<T> {
  const response = await fetch(`/api/v2/companies-house${path}`, { headers: authHeaders() });
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}));
    throw new Error(detail.detail ?? `Companies House request failed (${response.status})`);
  }
  return response.json();
}

/** Whether this deployment has a Companies House key. */
export async function companiesHouseConfigured(): Promise<boolean> {
  try {
    return (await companiesHouse<{ configured: boolean }>('/status')).configured;
  } catch {
    return false;
  }
}

/** Companies by name, or by any of the filters (advanced search). */
export function searchCompanies(query: string, filters: CompanySearchFilters = {}): Promise<CompanyHit[]> {
  const params = new URLSearchParams({ q: query });
  if (filters.status) params.set('status', filters.status);
  if (filters.type) params.set('type', filters.type);
  if (filters.sic) params.set('sic', filters.sic);
  if (filters.incorporatedFrom) params.set('incorporated_from', filters.incorporatedFrom);
  if (filters.incorporatedTo) params.set('incorporated_to', filters.incorporatedTo);
  return companiesHouse<CompanyHit[]>(`/search?${params.toString()}`);
}

/** A company's accounts filings, newest first. */
export function companyAccounts(number: string): Promise<AccountsFiling[]> {
  return companiesHouse<AccountsFiling[]>(`/company/${encodeURIComponent(number)}/accounts`);
}

/** Read a file as base64, without the data: URL prefix the API does not want. */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
}

/** Trigger a browser download without leaving the object URL behind. */
async function downloadBlob(blob: Blob, filename: string): Promise<void> {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Call an endpoint that returns the whole updated model, and adopt it.
 *
 * Every mutation endpoint responds with the recomputed model, so the client
 * never has to recompute or guess. A failure sets `saveError` rather than
 * throwing: the user is mid-edit, and an unhandled rejection would leave the
 * screen showing changes the server rejected.
 */
async function request(
  set: (fn: (state: FinancialModelV2State) => void) => void,
  get: () => FinancialModelV2State & FinancialModelV2Actions,
  url: string,
  init: RequestInit,
): Promise<FinancialModelV2 | null> {
  try {
    const response = await fetch(url, init);
    if (!response.ok) throw new Error(`Request failed (${response.status})`);
    const model = await response.json();
    set((state) => {
      state.model = model;
      state.isRecalculating = false;
      state.isDirty = false;
      state.saveError = null;
    });
    return model;
  } catch (error) {
    set((state) => {
      state.isRecalculating = false;
      state.saveError = error instanceof Error ? error.message : String(error);
    });
    return null;
  }
}

/**
 * Wipe everything scoped to a single model.
 *
 * Called when leaving a model AND when loading a different one. Without it the
 * backtest, balance findings, forecast and version list from the previous
 * model stay on screen under the new model's name — figures attributed to the
 * wrong company, which is worse than showing nothing.
 */
function clearModelScopedState(state: FinancialModelV2State): void {
  state.model = null;
  state.activeSheet = 'overview';
  state.versions = [];
  state.scenarios = [];
  state.diagnosis = null;
  state.attention = null;
  state.tabAudit = null;
  state.backtest = null;
  state.backtestYears = [];
  state.backtestError = null;
  state.backtestAnalysis = null;
  state.backtestAnalysisError = null;
  state.reconciliation = null;
  state.reconciliationError = null;
  state.forecast = null;
  state.forecastError = null;
  state.saveError = null;
  state.isDirty = false;
  state.isGeneratingSummary = false;
  state.isAnalysingBalance = false;
  state.isBacktesting = false;
  state.isForecasting = false;
  state.forecastProgress = '';
}

/** Walk a dotted path like 'staffing.directStaff.headcount'. */
function resolvePath(root: unknown, path: string): PeriodData | undefined {
  let node: any = root;
  for (const part of path.split('.')) {
    if (node == null) return undefined;
    node = node[part];
  }
  return node as PeriodData | undefined;
}

/**
 * The series at `path`, created where the model does not have it yet - an input a filing never
 * supplied (purchases, an entered tax charge) is absent, and an edit to it was silently dropped.
 */
function ensureSeries(root: unknown, path: string): PeriodData | undefined {
  let node: any = root;
  const parts = path.split('.');
  for (const [i, part] of parts.entries()) {
    if (node == null) return undefined;
    if (node[part] == null) node[part] = i === parts.length - 1 ? {} : {};
    node = node[part];
  }
  return node as PeriodData;
}

export interface FinancialModelV2State {
  model: FinancialModelV2 | null;
  models: ModelMetadataV2[];
  isLoading: boolean;
  isRecalculating: boolean;
  loadError: string | null;
  saveError: string | null;
  activeSheet: string;
  versions: ModelVersionMetaV2[];
  scenarios: ModelVersionMetaV2[];
  isGeneratingSummary: boolean;
  /** True while an edit is waiting on the debounce, i.e. not yet saved. */
  isDirty: boolean;
  diagnosis: BalanceDiagnosis | null;
  /** What to look at first - see AttentionItem. */
  attention: AttentionItem[] | null;
  /** Every tab checked against the others and the filing - see TabAudit. */
  tabAudit: TabAudit | null;
  /** The filing read a second time, explaining an imbalance. Applied by nobody. */
  balanceFromText: BalanceFromTextProposal | null;
  /** What the last correction from the filing changed, and whether it was enough. */
  balanceTextApplied: BalanceTextApplied | null;
  /** Every Autofix run on this model, oldest first. */
  autofixRuns: AutofixRun[];
  isAutofixing: boolean;
  autofixProgress: string;
  autofixError: string;
  isAnalysingBalance: boolean;
  backtestYears: number[];
  backtest: BacktestResultV2 | null;
  isBacktesting: boolean;
  backtestError: string | null;
  /** AI explanation of the last backtest. Null until asked for. */
  backtestAnalysis: string | null;
  isAnalysingBacktest: boolean;
  backtestAnalysisError: string | null;
  /** Engine-derived balances against the reported ones, per historical year. */
  reconciliation: ReconciliationResultV2 | null;
  isReconciling: boolean;
  reconciliationError: string | null;
  /** Progress message while a PDF import runs, or null when none is running. */
  forecast: ForecastResultV2 | null;
  isForecasting: boolean;
  /** What the forecast is doing right now, for the progress indicator. */
  forecastProgress: string;
  forecastError: string | null;
  pdfProgress: string | null;
  pdfError: string | null;
  /** Caveats the extractor raised about the imported figures. */
  pdfWarnings: string[];
}

export interface FinancialModelV2Actions {
  listModels: () => Promise<void>;
  loadModel: (id: string) => Promise<void>;
  /** Leave the open model. Resolves false if an unsaved edit could not be saved. */
  closeModel: () => Promise<boolean>;
  createModel: (name: string) => Promise<FinancialModelV2 | null>;
  deleteModel: (id: string) => Promise<void>;
  importModel: (file: File, name?: string) => Promise<FinancialModelV2 | null>;
  exportModel: () => Promise<void>;
  updateInput: (path: string, period: string, value: number) => void;
  /** Set one input that is a single value, not a series - a ratio, a setting held in the inputs,
   *  an opening balance - and save as `updateInput` does. */
  updateInputValue: (path: string, value: number | string) => void;
  /**
   * Whether the FILED figures can be edited.
   *
   * Locked by default and re-locked on reload, deliberately not persisted: unlocking is a
   * decision about the session in front of you, and a lock that silently stayed open is no
   * lock. See `V2EditLock`.
   */
  editingUnlocked: boolean;
  setEditingUnlocked: (unlocked: boolean) => void;
  updateSetting: <K extends keyof ModelSettings>(
    key: K,
    value: ModelSettings[K],
  ) => void;
  setOverride: (
    category: OverrideCategory,
    field: string,
    period: string,
    value: number | null,
  ) => Promise<void>;
  clearOverride: (
    category: OverrideCategory,
    field: string,
    period: string,
  ) => Promise<void>;
  clearAllOverrides: () => Promise<void>;
  resetInputs: () => Promise<void>;
  listVersions: () => Promise<void>;
  restoreVersion: (version: number) => Promise<void>;
  saveScenario: (name: string) => Promise<void>;
  deleteScenario: (version: number) => Promise<void>;
  exportVersion: (version: number) => Promise<void>;
  generateSummary: () => Promise<void>;
  /** Run the deterministic checks. No API key needed. */
  diagnoseBalance: () => Promise<void>;
  loadTabAudit: () => Promise<void>;
  /** Mark a figure the filing does not vouch for as looked at and right as it stands. */
  confirmFigure: (key: string) => Promise<void>;
  /** Fetch what to look at first. Deterministic, no API key. */
  loadAttention: () => Promise<void>;
  /** Run the accountant's review once more; the result is kept only if better. */
  runAutofix: () => Promise<void>;
  loadAutofixRuns: () => Promise<void>;
  readBalanceFromText: () => Promise<void>;
  applyBalanceFromText: () => Promise<void>;
  /** Run the checks AND have the AI explain them. */
  analyseBalance: () => Promise<void>;
  loadBacktestYears: () => Promise<void>;
  runBacktest: (holdoutYear: number) => Promise<void>;
  analyseBacktest: (holdoutYear: number) => Promise<void>;
  runReconciliation: () => Promise<void>;
  runForecast: (regenerate?: boolean) => Promise<void>;
  applyForecast: () => Promise<void>;
  importPdf: (file: File, options?: { reread?: boolean }) => Promise<void>;
  /** Import one accounts filing straight from Companies House - followed like an upload. */
  importFromCompaniesHouse: (
    number: string,
    transactionId: string,
    name: string,
    options?: { reread?: boolean },
  ) => Promise<void>;
  downloadTemplate: () => Promise<void>;
  dismissPdfReport: () => void;
  /**
   * Save any pending edit immediately instead of waiting out the debounce.
   * Resolves false if anything failed to save.
   */
  flushPendingSave: () => Promise<boolean>;
  updatePeriod: (change: {
    forecastStartYear?: number;
    numForecastYears?: number;
    periodType?: ModelSettings['periodType'];
    method?: 'third_month' | 'equal_spread';
  }) => Promise<void>;
  setActiveSheet: (sheet: string) => void;
  clearSaveError: () => void;
}

let recalcTimer: ReturnType<typeof setTimeout> | null = null;
let settingsTimer: ReturnType<typeof setTimeout> | null = null;

type V2Set = (fn: (state: FinancialModelV2State) => void) => void;
type V2Get = () => FinancialModelV2State & FinancialModelV2Actions;

function failImport(set: V2Set, message: string) {
  set((state) => {
    state.pdfProgress = null;
    state.pdfError = message;
  });
}

/**
 * Follow an import task the server started until it finishes - an uploaded PDF or
 * a filing fetched from Companies House, which run the same import. The cap is
 * generous because a scanned filing genuinely takes several minutes, but it is a
 * cap: without one a server that died mid-import would leave this spinning forever.
 */
async function followImport(started: Response, set: V2Set, get: V2Get) {
  if (!started.ok) {
    const detail = await started.json().catch(() => ({}));
    failImport(set, detail.detail ?? `Import failed (${started.status})`);
    return;
  }
  const { taskId } = await started.json();
  const POLL_MS = 2000;
  const MAX_POLLS = 450; // ~15 minutes
  for (let i = 0; i < MAX_POLLS; i += 1) {
    await new Promise((resolve) => setTimeout(resolve, POLL_MS));
    const response = await fetch(`/api/v2/models/import-pdf/${taskId}`, { headers: authHeaders() });
    if (!response.ok) {
      failImport(set, 'The import task was lost. Please try again.');
      return;
    }
    const task = await response.json();
    if (task.status === 'failed') {
      failImport(set, task.error ?? 'The import failed.');
      return;
    }
    if (task.status === 'complete') {
      set((state) => {
        state.pdfProgress = null;
        state.model = task.model;
        state.pdfWarnings = task.warnings ?? [];
      });
      await get().listModels();
      return;
    }
    set((state) => {
      state.pdfProgress = task.progress || 'Working…';
    });
  }
  failImport(set, 'The import is taking longer than expected. Please try again.');
}

export const useFinancialModelV2Store = create<
  FinancialModelV2State & FinancialModelV2Actions
>()(
  immer((set, get) => {
    /** Save the inputs after the debounce - one save for a burst of edits, whichever action made them. */
    function saveInputsSoon(): void {
      if (recalcTimer) clearTimeout(recalcTimer);
      recalcTimer = setTimeout(async () => {
        const current = get().model;
        if (!current) return;
        try {
          const response = await fetch(`/api/v2/models/${current.id}/inputs`, {
            method: 'PATCH',
            headers: jsonHeaders(),
            body: JSON.stringify({ inputs: current.inputs as ModelInputsV2 }),
          });
          if (!response.ok) throw new Error(`Save failed (${response.status})`);
          const updated = await response.json();
          set((state) => {
            state.model = updated;
            state.isRecalculating = false;
            state.isDirty = false;
            state.saveError = null;
          });
        } catch (error) {
          // Surface it: silently dropping a save leaves the user believing
          // edits are persisted.
          set((state) => {
            state.isRecalculating = false;
            state.saveError = error instanceof Error ? error.message : String(error);
          });
        }
      }, RECALC_DEBOUNCE_MS);
    }

    return {
    model: null,
    models: [],
    isLoading: false,
    isRecalculating: false,
    loadError: null,
    saveError: null,
    activeSheet: 'pnl',
    versions: [],
    scenarios: [],
    isGeneratingSummary: false,
    isDirty: false,
    editingUnlocked: false,
    diagnosis: null,
    attention: null,
    tabAudit: null,
    balanceFromText: null,
    balanceTextApplied: null,
    autofixRuns: [],
    isAutofixing: false,
    autofixProgress: '',
    autofixError: '',
    isAnalysingBalance: false,
    backtestYears: [],
    backtest: null,
    backtestAnalysis: null,
    isAnalysingBacktest: false,
    backtestAnalysisError: null,
    reconciliation: null,
    isReconciling: false,
    reconciliationError: null,
    isBacktesting: false,
    backtestError: null,
    forecast: null,
    isForecasting: false,
    forecastProgress: '',
    forecastError: null,
    pdfProgress: null,
    pdfError: null,
    pdfWarnings: [],

    setActiveSheet: (sheet) =>
      set((state) => {
        state.activeSheet = sheet;
      }),

    clearSaveError: () =>
      set((state) => {
        state.saveError = null;
      }),

    listModels: async () => {
      set((state) => {
        state.isLoading = true;
        state.loadError = null;
      });
      try {
        const response = await fetch('/api/v2/models', { headers: authHeaders() });
        if (!response.ok) throw new Error(`Failed to list models (${response.status})`);
        const models = await response.json();
        set((state) => {
          state.models = models;
          state.isLoading = false;
        });
      } catch (error) {
        set((state) => {
          state.loadError = error instanceof Error ? error.message : String(error);
          state.isLoading = false;
        });
      }
    },

    loadModel: async (id) => {
      set((state) => {
        // Drop the previous model's analysis before the new one arrives.
        clearModelScopedState(state);
        state.isLoading = true;
        state.loadError = null;
      });
      try {
        const response = await fetch(`/api/v2/models/${id}`, { headers: authHeaders() });
        if (!response.ok) throw new Error(`Failed to load model (${response.status})`);
        const model = await response.json();
        set((state) => {
          state.model = model;
          // The forecast is SAVED on the model, but the AI Forecast tab reads
          // this field, which only a run in the current session filled. So
          // reopening a model showed "Run forecast" with the enterprise value
          // range and the posteriors gone, although the whole distribution was
          // sitting on the row - inviting a re-run that costs the full
          // pipeline. Hydrating here is what makes a saved forecast readable.
          state.forecast = hydrateForecast(model.forecastDistribution);
          state.isLoading = false;
        });
      } catch (error) {
        set((state) => {
          state.loadError = error instanceof Error ? error.message : String(error);
          state.isLoading = false;
        });
      }
    },

    closeModel: async () => {
      // A debounced edit may still be pending. The timer reads the model when
      // it fires and bails if there is none, so clearing first would discard
      // the edit silently - the user would return to find their last typing
      // gone. Flush it, and stay put if the save fails rather than navigating
      // away from work that was never persisted.
      if (get().isDirty && !(await get().flushPendingSave())) {
        return false;
      }
      set((state) => {
        clearModelScopedState(state);
      });
      return true;
    },

    createModel: async (name) => {
      const response = await fetch('/api/v2/models', {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ name }),
      });
      if (!response.ok) return null;
      const model = await response.json();
      set((state) => {
        state.model = model;
      });
      await get().listModels();
      return model;
    },

    deleteModel: async (id) => {
      await fetch(`/api/v2/models/${id}`, { method: 'DELETE', headers: authHeaders() });
      set((state) => {
        if (state.model?.id === id) state.model = null;
      });
      await get().listModels();
    },

    importModel: async (file, name) => {
      const form = new FormData();
      form.append('file', file);
      const query = name ? `?name=${encodeURIComponent(name)}` : '';
      const response = await fetch(`/api/v2/models/import${query}`, {
        method: 'POST',
        headers: authHeaders(),
        body: form,
      });
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        set((state) => {
          state.loadError = detail.detail ?? `Import failed (${response.status})`;
        });
        return null;
      }
      const model = await response.json();
      set((state) => {
        state.model = model;
      });
      await get().listModels();
      return model;
    },

    exportModel: async () => {
      const model = get().model;
      if (!model) return;
      const response = await fetch(`/api/v2/models/${model.id}/export`, {
        headers: authHeaders(),
      });
      if (!response.ok) return;
      await downloadBlob(await response.blob(), `${model.name}-v2.xlsx`);
    },

    setEditingUnlocked: (unlocked) => {
      set((state) => {
        state.editingUnlocked = unlocked;
      });
    },

    updateInputValue: (path, value) => {
      if (!get().model) return;
      set((state) => {
        if (!state.model) return;
        const parts = path.split('.');
        let node: any = state.model.inputs;
        for (const part of parts.slice(0, -1)) {
          if (node[part] == null) node[part] = {};
          node = node[part];
        }
        node[parts[parts.length - 1]] = value;
        state.isRecalculating = true;
        state.isDirty = true;
      });
      saveInputsSoon();
    },

    updateInput: (path, period, value) => {
      const model = get().model;
      if (!model) return;

      // Optimistic: the cell must respond immediately.
      set((state) => {
        if (!state.model) return;
        const target = ensureSeries(state.model.inputs, path);
        if (target) target[period] = value;
        state.isRecalculating = true;
        state.isDirty = true;
      });
      saveInputsSoon();
    },

    updateSetting: (key, value) => {
      const model = get().model;
      if (!model) return;

      // Optimistic, like updateInput: a settings control that lags a round
      // trip behind the user reads as broken.
      set((state) => {
        if (!state.model) return;
        (state.model.settings as Record<string, unknown>)[key] = value;
        state.isRecalculating = true;
        state.isDirty = true;
      });

      if (settingsTimer) clearTimeout(settingsTimer);
      settingsTimer = setTimeout(async () => {
        const current = get().model;
        if (!current) return;
        await request(set, get, `/api/v2/models/${current.id}/settings`, {
          method: 'PATCH',
          headers: jsonHeaders(),
          body: JSON.stringify({ settings: current.settings }),
        });
      }, RECALC_DEBOUNCE_MS);
    },

    setOverride: async (category, field, period, value) => {
      const model = get().model;
      if (!model) return;
      await request(set, get, `/api/v2/models/${model.id}/overrides`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ category, field, period, value }),
      });
    },

    clearOverride: async (category, field, period) => {
      await get().setOverride(category, field, period, null);
    },

    clearAllOverrides: async () => {
      const model = get().model;
      if (!model) return;
      await request(set, get, `/api/v2/models/${model.id}/overrides`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
    },

    resetInputs: async () => {
      const model = get().model;
      if (!model) return;
      await request(set, get, `/api/v2/models/${model.id}/reset-inputs`, {
        method: 'POST',
        headers: authHeaders(),
      });
    },

    listVersions: async () => {
      const model = get().model;
      if (!model) return;
      try {
        const [versions, scenarios] = await Promise.all([
          fetch(`/api/v2/models/${model.id}/versions`, { headers: authHeaders() }),
          fetch(`/api/v2/models/${model.id}/scenarios`, { headers: authHeaders() }),
        ]);
        if (!versions.ok || !scenarios.ok) return;
        const [versionList, scenarioList] = await Promise.all([
          versions.json(),
          scenarios.json(),
        ]);
        set((state) => {
          state.versions = versionList;
          state.scenarios = scenarioList;
        });
      } catch {
        // A failed history fetch must not take the workspace down with it —
        // the model on screen is still valid, only the side panel is empty.
      }
    },

    restoreVersion: async (version) => {
      const model = get().model;
      if (!model) return;
      await request(set, get, `/api/v2/models/${model.id}/versions/${version}/restore`, {
        method: 'POST',
        headers: authHeaders(),
      });
      await get().listVersions();
    },

    saveScenario: async (name) => {
      const model = get().model;
      if (!model) return;
      const response = await fetch(`/api/v2/models/${model.id}/scenarios`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ name }),
      });
      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        set((state) => {
          state.saveError = detail.detail ?? `Could not save scenario (${response.status})`;
        });
        return;
      }
      // The version counter moved on the server; reload so the header and any
      // later restore act on the real number rather than a stale one.
      await get().loadModel(model.id);
      await get().listVersions();
    },

    deleteScenario: async (version) => {
      const model = get().model;
      if (!model) return;
      await fetch(`/api/v2/models/${model.id}/scenarios/${version}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      await get().listVersions();
    },

    exportVersion: async (version) => {
      const model = get().model;
      if (!model) return;
      const response = await fetch(
        `/api/v2/models/${model.id}/versions/${version}/export`,
        { headers: authHeaders() },
      );
      if (!response.ok) return;
      await downloadBlob(await response.blob(), `${model.name}-v2-v${version}.xlsx`);
    },

    updatePeriod: async (change) => {
      // NOT a settings patch. Periods are the keys the figures are stored
      // under, so this endpoint re-keys the data as it changes them. Patching
      // periodType directly would leave every input under a key the new
      // context never reads - the figures would look deleted.
      const model = get().model;
      if (!model) return;
      set((state) => {
        state.isRecalculating = true;
      });
      await request(set, get, `/api/v2/models/${model.id}/update-period`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify(change),
      });
    },

    /**
     * Read the filing again and ask which figure is probably wrong.
     *
     * Deterministic, instant and free - no API call behind it - so it is fetched like the
     * diagnosis rather than hidden behind a button. It proposes and applies nothing: there
     * is deliberately no endpoint that adopts a candidate, because these are the user's own
     * figures and the imbalance is the evidence that one of them is wrong.
     */
    readBalanceFromText: async () => {
      const model = get().model;
      if (!model) return;
      const response = await fetch(
        `/api/v2/models/${model.id}/balance-from-text`,
        { headers: authHeaders() },
      );
      if (!response.ok) return;
      const proposal = await response.json();
      set((state) => {
        state.balanceFromText = proposal;
      });
    },

    /**
     * Take the filing's own figures where they disagree with the model's.
     *
     * The automated correction, and a different thing from `generateBalanceFix` below. That
     * one asks a model to nudge assumptions until the sheet comes out level; this changes only
     * figures that were read wrongly and takes the replacement from the filing, which is the
     * source. It does not promise a balanced sheet, and the result carries the check before and
     * after so the screen cannot pretend otherwise.
     */
    applyBalanceFromText: async () => {
      const model = get().model;
      if (!model) return;
      const response = await fetch(
        `/api/v2/models/${model.id}/balance-from-text/apply`,
        {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        },
      );
      if (!response.ok) return;
      const result = await response.json();
      set((state) => {
        state.model = result.model;
        state.balanceTextApplied = result.applied;
      });
      // The proposal described the figures as they were a moment ago.
      await get().readBalanceFromText();
    },

    loadAutofixRuns: async () => {
      const model = get().model;
      if (!model) return;
      const response = await fetch(`/api/v2/models/${model.id}/autofix-status`, {
        headers: authHeaders(),
      });
      if (!response.ok) return;
      const status = await response.json();
      set((state) => {
        state.autofixRuns = status.runs ?? [];
        state.isAutofixing = status.status === 'running';
      });
    },

    /**
     * One Autofix run. The server reviews the model as it stands - the user's own
     * figures locked - and keeps the result only if it scores better than the model
     * already does; every run is recorded either way. Polled by MODEL, as the forecast
     * is: the status lives on the model row, which either worker can read.
     */
    runAutofix: async () => {
      const model = get().model;
      if (!model || get().isAutofixing) return;
      set((state) => {
        state.isAutofixing = true;
        state.autofixError = '';
        state.autofixProgress = 'Starting…';
      });
      try {
        const started = await fetch(`/api/v2/models/${model.id}/autofix/start`, {
          method: 'POST',
          headers: authHeaders(),
        });
        if (!started.ok) {
          set((state) => {
            state.autofixError = `Autofix could not start (${started.status}).`;
          });
          return;
        }
        const deadline = Date.now() + 30 * 60 * 1000;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 3000));
          const poll = await fetch(`/api/v2/models/${model.id}/autofix-status`, {
            headers: authHeaders(),
          });
          if (!poll.ok) {
            set((state) => {
              state.autofixError = 'Lost track of Autofix - please retry.';
            });
            return;
          }
          const task = await poll.json();
          set((state) => {
            state.autofixProgress = task.progress ?? '';
            state.autofixRuns = task.runs ?? [];
          });
          if (task.status === 'error') {
            set((state) => {
              state.autofixError = task.error ?? 'Autofix failed.';
            });
            return;
          }
          if (task.status === 'done') {
            // A kept run changed the model: load it, and what depends on it.
            await get().loadModel(model.id);
            await get().loadAttention();
            return;
          }
        }
        set((state) => {
          state.autofixError = 'Autofix is taking longer than expected - check back shortly.';
        });
      } finally {
        set((state) => {
          state.isAutofixing = false;
          state.autofixProgress = '';
        });
      }
    },

    loadTabAudit: async () => {
      const model = get().model;
      if (!model) return;
      const response = await fetch(`/api/v2/models/${model.id}/audit`, {
        headers: authHeaders(),
      });
      if (!response.ok) return;
      const audit = await response.json();
      set((state) => {
        state.tabAudit = audit;
      });
    },

    confirmFigure: async (key) => {
      const model = get().model;
      if (!model) return;
      const confirmed = [...(model.inputs.confirmedFigures ?? [])];
      if (confirmed.includes(key)) return;
      confirmed.push(key);
      const inputs = { ...model.inputs, confirmedFigures: confirmed } as ModelInputsV2;
      try {
        const response = await fetch(`/api/v2/models/${model.id}/inputs`, {
          method: 'PATCH',
          headers: jsonHeaders(),
          body: JSON.stringify({ inputs }),
        });
        if (!response.ok) throw new Error(`Save failed (${response.status})`);
        const updated = await response.json();
        set((state) => {
          state.model = updated;
          state.saveError = null;
        });
      } catch (error) {
        set((state) => {
          state.saveError = error instanceof Error ? error.message : String(error);
        });
      }
    },

    loadAttention: async () => {
      const model = get().model;
      if (!model) return;
      const response = await fetch(`/api/v2/models/${model.id}/attention`, {
        headers: authHeaders(),
      });
      if (!response.ok) return;
      const attention = await response.json();
      set((state) => {
        state.attention = attention;
      });
    },

    diagnoseBalance: async () => {
      const model = get().model;
      if (!model) return;
      const response = await fetch(
        `/api/v2/models/${model.id}/diagnose-balance`,
        { headers: authHeaders() },
      );
      if (!response.ok) return;
      const diagnosis = await response.json();
      set((state) => {
        state.diagnosis = diagnosis;
      });
    },

    analyseBalance: async () => {
      const model = get().model;
      if (!model) return;
      set((state) => {
        state.isAnalysingBalance = true;
      });
      try {
        const response = await fetch(
          `/api/v2/models/${model.id}/analyze-balance`,
          { method: 'POST', headers: authHeaders() },
        );
        if (!response.ok) return;
        const body = await response.json();
        set((state) => {
          // The findings arrive whether or not the AI answered, so they are
          // stored unconditionally. Only the prose is conditional.
          state.diagnosis = body.diagnosis;
          if (state.model) state.model.aiBalanceAnalysis = body.analysis;
        });
      } finally {
        set((state) => {
          state.isAnalysingBalance = false;
        });
      }
    },

    loadBacktestYears: async () => {
      const model = get().model;
      if (!model) return;
      const response = await fetch(
        `/api/v2/models/${model.id}/backtest/years`,
        { headers: authHeaders() },
      );
      if (!response.ok) return;
      set((state) => {
        state.backtestYears = [];
      });
      const years = await response.json();
      set((state) => {
        state.backtestYears = years;
      });
    },

    runBacktest: async (holdoutYear) => {
      const model = get().model;
      if (!model) return;
      set((state) => {
        state.isBacktesting = true;
        state.backtestError = null;
      });
      try {
        const response = await fetch(`/api/v2/models/${model.id}/backtest`, {
          method: 'POST',
          headers: jsonHeaders(),
          body: JSON.stringify({ holdoutYear }),
        });
        if (!response.ok) {
          const detail = await response.json().catch(() => ({}));
          set((state) => {
            // The server explains WHY a year cannot be held out; surfacing
            // that beats a generic failure the user cannot act on.
            state.backtestError =
              detail.detail ?? `Backtest failed (${response.status})`;
            state.backtest = null;
          });
          return;
        }
        const result = await response.json();
        set((state) => {
          state.backtest = result;
          // The previous explanation described a different run. Keeping it
          // beside new numbers would be worse than having none.
          state.backtestAnalysis = null;
          state.backtestAnalysisError = null;
        });
      } finally {
        set((state) => {
          state.isBacktesting = false;
        });
      }
    },

    analyseBacktest: async (holdoutYear) => {
      const model = get().model;
      if (!model) return;
      set((state) => {
        state.isAnalysingBacktest = true;
        state.backtestAnalysisError = null;
      });
      try {
        const response = await fetch(
          `/api/v2/models/${model.id}/backtest/analyze`,
          {
            method: 'POST',
            headers: jsonHeaders(),
            body: JSON.stringify({ holdoutYear }),
          },
        );
        if (!response.ok) {
          const detail = await response.json().catch(() => ({}));
          set((state) => {
            state.backtestAnalysisError =
              detail.detail ?? `Analysis failed (${response.status})`;
          });
          return;
        }
        const data = await response.json();
        set((state) => {
          // A null analysis means no API key is configured. The backtest is
          // arithmetic and stands without it, so this is a missing
          // commentary rather than a failure.
          state.backtestAnalysis = data.analysis ?? null;
          state.backtestAnalysisError =
            data.analysis == null
              ? 'No analysis was returned. The server has no API key configured.'
              : null;
        });
      } finally {
        set((state) => {
          state.isAnalysingBacktest = false;
        });
      }
    },

    runReconciliation: async () => {
      const model = get().model;
      if (!model) return;
      set((state) => {
        state.isReconciling = true;
        state.reconciliationError = null;
      });
      try {
        const response = await fetch(
          `/api/v2/models/${model.id}/backtest/historical-reconciliation`,
          { method: 'POST', headers: jsonHeaders() },
        );
        if (!response.ok) {
          const detail = await response.json().catch(() => ({}));
          set((state) => {
            state.reconciliationError =
              detail.detail ?? `Reconciliation failed (${response.status})`;
            state.reconciliation = null;
          });
          return;
        }
        // An empty result is a real answer - a model built from ratios has
        // no reported balances to check - and carries notes saying so, so it
        // is stored rather than treated as a failure.
        const result = await response.json();
        set((state) => {
          state.reconciliation = result;
        });
      } finally {
        set((state) => {
          state.isReconciling = false;
        });
      }
    },

    runForecast: async (regenerate = false) => {
      const model = get().model;
      if (!model) return;
      set((state) => {
        state.isForecasting = true;
        state.forecastError = null;
        state.forecastProgress = 'Starting…';
      });
      try {
        // Started as a background task rather than awaited over one request.
        // The pipeline runs longer than the gateway allows - about 180s
        // against a 120s limit - so the synchronous call always came back 504
        // even though the work had completed and the result was saved.
        const started = await fetch(
          `/api/v2/models/${model.id}/forecast/start?regenerate=${regenerate}`,
          { method: 'POST', headers: authHeaders() },
        );
        if (!started.ok) {
          const detail = await started.json().catch(() => ({}));
          set((state) => {
            state.forecastError =
              detail.detail ?? `Forecast failed (${started.status})`;
          });
          return;
        }
        await started.json();

        // Poll until it finishes. The deadline is generous because the work
        // genuinely takes minutes; it exists so a lost task cannot spin here
        // for ever.
        const deadline = Date.now() + 10 * 60 * 1000;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 2500));
          // Polled by MODEL, not by task id. The status lives on the model
          // row because the service runs two uvicorn workers — an in-memory
          // task was invisible to the other one, and a poll that landed there
          // got a 404 that CloudFront served as the SPA's index page.
          const poll = await fetch(`/api/v2/models/${model.id}/forecast-status`, {
            headers: authHeaders(),
          });
          if (!poll.ok) {
            set((state) => {
              state.forecastError = 'Lost track of the forecast — please retry.';
            });
            return;
          }
          const task = await poll.json();
          set((state) => {
            state.forecastProgress = task.progress ?? '';
          });
          if (task.status === 'done') {
            set((state) => {
              state.forecast = task.distribution;
              state.forecastProgress = '';
              if (state.model) {
                state.model.forecastDistribution = task.distribution;
              }
            });
            return;
          }
          if (task.status === 'error') {
            set((state) => {
              state.forecastError = task.error ?? 'The forecast failed.';
            });
            return;
          }
        }
        set((state) => {
          state.forecastError =
            'The forecast is taking longer than expected — it may still finish; reopen the model shortly.';
        });
      } finally {
        set((state) => {
          state.isForecasting = false;
          state.forecastProgress = '';
        });
      }
    },

    applyForecast: async () => {
      const model = get().model;
      if (!model) return;
      await request(set, get, `/api/v2/models/${model.id}/forecast/apply`, {
        method: 'POST',
        headers: authHeaders(),
      });
    },

    dismissPdfReport: () =>
      set((state) => {
        state.pdfError = null;
        state.pdfWarnings = [];
      }),

    downloadTemplate: async () => {
      // IAS accounting's own template: the engine's layout, blank, with live formulas.
      // Baseline Accounting's template is the source workbook, corrected - a different file.
      const response = await fetch('/api/v2/models/template', {
        headers: authHeaders(),
      });
      if (!response.ok) {
        set((state) => {
          state.loadError = `Could not download the template (${response.status})`;
        });
        return;
      }
      await downloadBlob(await response.blob(), 'IAS-accounting-template.xlsx');
    },

    importPdf: async (file, options) => {
      // Extraction takes minutes on a scanned filing, so the server runs it in
      // the background and this polls. A single long request would die at
      // whatever timeout sits between the browser and the app.
      set((state) => {
        state.pdfProgress = 'Uploading…';
        state.pdfError = null;
        state.pdfWarnings = [];
      });
      try {
        const base64 = await fileToBase64(file);
        const started = await fetch('/api/v2/models/import-pdf', {
          method: 'POST',
          headers: jsonHeaders(),
          body: JSON.stringify({ filename: file.name, file: base64, reread: options?.reread ?? false }),
        });
        await followImport(started, set, get);
      } catch (error) {
        failImport(set, error instanceof Error ? error.message : String(error));
      }
    },

    importFromCompaniesHouse: async (number, transactionId, name, options) => {
      // The server fetches the filing and runs the same import an upload does,
      // so it is followed exactly as one.
      set((state) => {
        state.pdfProgress = 'Fetching from Companies House…';
        state.pdfError = null;
        state.pdfWarnings = [];
      });
      try {
        const started = await fetch(
          `/api/v2/companies-house/company/${encodeURIComponent(number)}/import`,
          {
            method: 'POST',
            headers: jsonHeaders(),
            body: JSON.stringify({ transactionId, name, reread: options?.reread ?? false }),
          },
        );
        await followImport(started, set, get);
      } catch (error) {
        failImport(set, error instanceof Error ? error.message : String(error));
      }
    },

    flushPendingSave: async () => {
      // The debounce exists so a spreadsheet does not fire a request per
      // keystroke. A button press is a deliberate 'now', so it cancels the
      // timer and sends immediately rather than adding a second request
      // behind the one already scheduled.
      const model = get().model;
      // Nothing open means nothing pending — vacuously saved.
      if (!model) return true;
      if (settingsTimer) {
        clearTimeout(settingsTimer);
        settingsTimer = null;
      }
      if (recalcTimer) {
        clearTimeout(recalcTimer);
        recalcTimer = null;
      }
      set((state) => {
        state.isRecalculating = true;
      });
      const savedSettings = await request(
        set,
        get,
        `/api/v2/models/${model.id}/settings`,
        {
          method: 'PATCH',
          headers: jsonHeaders(),
          body: JSON.stringify({ settings: model.settings }),
        },
      );
      const savedInputs = await request(
        set,
        get,
        `/api/v2/models/${model.id}/inputs`,
        {
          method: 'PATCH',
          headers: jsonHeaders(),
          body: JSON.stringify({ inputs: model.inputs }),
        },
      );

      const ok = savedSettings !== null && savedInputs !== null;
      if (!ok) {
        set((state) => {
          // request() clears saveError on success, so a failed settings save
          // followed by a successful inputs save would erase the message and
          // the failure would look like a success.
          state.saveError =
            state.saveError ?? 'Some changes could not be saved.';
        });
      }
      return ok;
    },

    generateSummary: async () => {
      const model = get().model;
      if (!model) return;
      set((state) => {
        state.isGeneratingSummary = true;
      });
      try {
        const response = await fetch(`/api/v2/models/${model.id}/generate-summary`, {
          method: 'POST',
          headers: authHeaders(),
        });
        if (response.ok) {
          const body = await response.json();
          set((state) => {
            if (state.model) state.model.aiSummary = body.summary;
          });
        }
      } finally {
        set((state) => {
          state.isGeneratingSummary = false;
        });
      }
    },
    };
  }),
);
